import { Plus } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { formatRM, parseRM } from '@/features/billing/money';
import {
  KIND_LABEL,
  PRACTITIONER_KINDS,
  type PractitionerKind,
  type SessionType,
  useSessionTypes,
  useWellbeingPractitioners,
  useWellbeingSetupActions,
} from '@/features/wellbeing-setup/api';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import local from '@/pages/finance/finance.module.css';
import styles from '@/pages/queue/queue.module.css';

const MODE_LABEL = { in_person: 'In person', video: 'Video' } as const;

function useFail() {
  const toast = useToast();
  return (err: unknown) =>
    toast.show({
      tone: 'error',
      title: 'Not saved',
      body: isApiError(err) ? err.message : undefined,
      error: err,
    });
}

/**
 * RL-1876 (gap #91): the Wellbeing set-up the practitioners run themselves
 * (decision D16): session types (name, kind, length, price, modes) and which
 * Mental health staff are booked as which kind of practitioner.
 */
export function WellbeingSetupPage() {
  const types = useSessionTypes(true);
  const practitioners = useWellbeingPractitioners();
  const actions = useWellbeingSetupActions();
  const toast = useToast();
  const fail = useFail();
  const [editing, setEditing] = useState<SessionType | 'new' | null>(null);
  return (
    <PageContainer>
      <Card
        title="Session types"
        flush
        actions={
          <Button
            size="sm"
            variant="primary"
            icon={<Plus size={16} />}
            onClick={() => setEditing('new')}
          >
            Add session type
          </Button>
        }
      >
        <DataTable
          caption="Session types"
          rows={types.data}
          rowKey={(t) => t.id}
          loading={types.isLoading}
          error={types.error}
          onRetry={() => void types.refetch()}
          empty={
            <EmptyState
              title="No session types"
              body="Add the sessions the clinic offers, with their price."
            />
          }
          columns={[
            {
              key: 'name',
              header: 'Session',
              render: (t) => (
                <>
                  <p className={styles.name}>{t.name}</p>
                  <p className={styles.sub}>{t.nameMs}</p>
                </>
              ),
            },
            {
              key: 'kind',
              header: 'Practitioner',
              render: (t) => KIND_LABEL[t.practitionerKind],
            },
            {
              key: 'length',
              header: 'Length',
              render: (t) => `${t.durationMinutes} min`,
            },
            {
              key: 'price',
              header: 'Price',
              align: 'right',
              render: (t) => formatRM(t.priceSen),
            },
            {
              key: 'modes',
              header: 'How',
              render: (t) => t.modes.map((m) => MODE_LABEL[m]).join(', '),
            },
            {
              key: 'status',
              header: 'Status',
              render: (t) =>
                t.active ? (
                  <Badge tone="success">Offered</Badge>
                ) : (
                  <Badge>Retired</Badge>
                ),
            },
            {
              key: 'act',
              header: <span className="visually-hidden">Actions</span>,
              align: 'right',
              render: (t) => (
                <span className={local.acts}>
                  <Button size="sm" onClick={() => setEditing(t)}>
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    loading={
                      actions.setActive.isPending &&
                      actions.setActive.variables?.id === t.id
                    }
                    onClick={() =>
                      void actions.setActive
                        .mutateAsync({ id: t.id, active: !t.active })
                        .then(() =>
                          toast.show({
                            tone: 'success',
                            title: t.active ? 'Retired' : 'Offered again',
                          }),
                        )
                        .catch(fail)
                    }
                  >
                    {t.active ? 'Retire' : 'Offer again'}
                  </Button>
                </span>
              ),
            },
          ]}
        />
      </Card>
      <Card title="Practitioners" flush>
        <DataTable
          caption="Practitioners"
          rows={practitioners.data}
          rowKey={(p) => p.staffUserId}
          loading={practitioners.isLoading}
          error={practitioners.error}
          onRetry={() => void practitioners.refetch()}
          empty={
            <EmptyState
              title="No mental health staff"
              body="Staff with the Mental health role appear here."
            />
          }
          columns={[
            { key: 'name', header: 'Name', render: (p) => p.name },
            {
              key: 'kinds',
              header: 'Booked as',
              render: (p) => (
                <ChipToggle
                  label={`Booked as · ${p.name}`}
                  options={PRACTITIONER_KINDS.map((k) => ({
                    value: k,
                    label: KIND_LABEL[k],
                  }))}
                  selected={p.kinds}
                  onChange={(kinds) =>
                    void actions.setKinds
                      .mutateAsync({ staffUserId: p.staffUserId, kinds })
                      .then(() =>
                        toast.show({ tone: 'success', title: 'Saved' }),
                      )
                      .catch(fail)
                  }
                />
              ),
            },
          ]}
        />
      </Card>
      {editing && (
        <SessionTypeDialog
          type={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </PageContainer>
  );
}

function SessionTypeDialog({
  type,
  onClose,
}: {
  type: SessionType | null;
  onClose: () => void;
}) {
  const actions = useWellbeingSetupActions();
  const toast = useToast();
  const fail = useFail();
  const [kind, setKind] = useState<PractitionerKind>(
    type?.practitionerKind ?? 'counsellor',
  );
  const [name, setName] = useState(type?.name ?? '');
  const [nameMs, setNameMs] = useState(type?.nameMs ?? '');
  const [price, setPrice] = useState(
    type ? (type.priceSen / 100).toFixed(2) : '',
  );
  const [minutes, setMinutes] = useState(String(type?.durationMinutes ?? 60));
  const [modes, setModes] = useState<('in_person' | 'video')[]>(
    type?.modes ?? ['in_person'],
  );
  const saving = actions.create.isPending || actions.update.isPending;
  const submit = () => {
    const priceSen = parseRM(price);
    const durationMinutes = Number(minutes);
    if (!name.trim() || !nameMs.trim())
      return toast.show({
        tone: 'error',
        title: 'Give the name in both languages',
      });
    if (priceSen === null)
      return toast.show({ tone: 'error', title: 'Check the price' });
    if (!modes.length)
      return toast.show({
        tone: 'error',
        title: 'Choose in person, video or both',
      });
    const body = {
      name: name.trim(),
      nameMs: nameMs.trim(),
      priceSen,
      durationMinutes,
      modes,
    };
    void (
      type
        ? actions.update.mutateAsync({ id: type.id, ...body })
        : actions.create.mutateAsync({ ...body, practitionerKind: kind })
    )
      .then(() => {
        toast.show({
          tone: 'success',
          title: type ? 'Session type saved' : 'Session type added',
        });
        onClose();
      })
      .catch(fail);
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={type ? `Edit · ${type.name}` : 'Add session type'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      <div className={local.form}>
        {type ? (
          <p className={local.note}>
            {`${KIND_LABEL[type.practitionerKind]}. The kind is fixed: retire this type and add another to change it. A new price applies to sessions booked from now on.`}
          </p>
        ) : (
          <Field label="Practitioner">
            <Select
              value={kind}
              options={PRACTITIONER_KINDS.map((k) => ({
                value: k,
                label: KIND_LABEL[k],
              }))}
              onChange={(e) => setKind(e.target.value as PractitionerKind)}
            />
          </Field>
        )}
        <Field label="Name (English)">
          <Input
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Name (Malay)">
          <Input
            value={nameMs}
            maxLength={80}
            onChange={(e) => setNameMs(e.target.value)}
          />
        </Field>
        <Field label="Price (RM)">
          <Input
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </Field>
        <Field label="Length">
          <Select
            value={minutes}
            options={Array.from({ length: 12 }, (_, i) => (i + 1) * 15).map(
              (m) => ({ value: String(m), label: `${m} min` }),
            )}
            onChange={(e) => setMinutes(e.target.value)}
          />
        </Field>
        <ChipToggle
          label="How"
          options={[
            { value: 'in_person', label: 'In person' },
            { value: 'video', label: 'Video' },
          ]}
          selected={modes}
          onChange={(v) => setModes(v as ('in_person' | 'video')[])}
        />
      </div>
    </Dialog>
  );
}
